import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { FromPMHandlers } from '../types';

import { paragraph } from './paragraph';
import { heading } from './heading';
import { text } from './text';
import { hardBreak } from './break';
import { thematicBreak } from './thematicBreak';
import { image } from './image';
import { media, mediaSingle } from './media';
import { codeBlock } from './code';
import { blockquote } from './blockquote';
import { table } from './table';
import { tableRow } from './tableRow';
import { tableCell } from './tableCell';
import { convertList } from './list';
import { convertTaskItem } from './listItem';
import { convertInlineChildren } from '../utils';

/**
 * Default handlers for converting ProseMirror nodes to mdast.
 *
 * Handlers that need access to the full handler map receive it via
 * a closure over `fromPMHandlers` so they can recursively convert children.
 */
export const fromPMHandlers: FromPMHandlers = {
	paragraph: (node: PMNode) => paragraph(node, fromPMHandlers),
	heading: (node: PMNode) => heading(node, fromPMHandlers),

	text: (node: PMNode) => text(node),
	hard_break: (_node: PMNode) => hardBreak(),
	hardBreak: (_node: PMNode) => hardBreak(),
	horizontal_rule: (_node: PMNode) => thematicBreak(),
	rule: (_node: PMNode) => thematicBreak(),

	image: (node: PMNode) => image(node),

	// `media` and `mediaSingle` map to a CommonMark image. The default
	// handler only knows how to serialise `type: "external"` media (which
	// has a public URL); `type: "file"` media is dropped because we don't
	// know the tenant or content scope here. Use `createMediaHandlers({
	// serializeFileMedia })` from './handlers/media' to opt in to
	// proxy-URL serialisation for file media (e.g. from Confluence).
	media: (node: PMNode) => media(node),
	mediaSingle: (node: PMNode) => mediaSingle(node),
	media_single: (node: PMNode) => mediaSingle(node),

	code_block: (node: PMNode) => codeBlock(node),
	codeBlock: (node: PMNode) => codeBlock(node),

	blockquote: (node: PMNode) => blockquote(node, fromPMHandlers),
	block_quote: (node: PMNode) => blockquote(node, fromPMHandlers),

	table: (node: PMNode) => table(node, fromPMHandlers),
	tableRow: (node: PMNode) => tableRow(node, fromPMHandlers),
	tableCell: (node: PMNode) => tableCell(node, fromPMHandlers),
	tableHeader: (node: PMNode) => tableCell(node, fromPMHandlers),

	bullet_list: (node: PMNode) => convertList(node, false, fromPMHandlers),
	bulletList: (node: PMNode) => convertList(node, false, fromPMHandlers),
	ordered_list: (node: PMNode) => convertList(node, true, fromPMHandlers),
	orderedList: (node: PMNode) => convertList(node, true, fromPMHandlers),
	task_list: (node: PMNode) => convertList(node, false, fromPMHandlers),
	taskList: (node: PMNode) => convertList(node, false, fromPMHandlers),

	list_item: (node: PMNode) => convertTaskItem(node, fromPMHandlers),
	listItem: (node: PMNode) => convertTaskItem(node, fromPMHandlers),
	// task_item (snake_case ADF) also uses convertTaskItem so checked/state attrs are read
	task_item: (node: PMNode) => convertTaskItem(node, fromPMHandlers),

	// ADF taskItem has inline content directly (no paragraph wrapper),
	// so we wrap it in a paragraph to satisfy the listItem → block content requirement.
	taskItem: (node: PMNode) => {
		const checked =
			node.attrs.state === 'DONE'
				? true
				: node.attrs.state === 'TODO'
					? false
					: (node.attrs.checked ?? null);
		return {
			type: 'listItem' as const,
			spread: false,
			checked,
			children: [
				{ type: 'paragraph' as const, children: convertInlineChildren(node, fromPMHandlers) },
			],
		};
	},
};
